import { getReportDataSource, METRIC_TYPES } from './reportSchema';

/** Collections supported by generateReportData (must match functions/reports/dataAggregator.js). */
export const REPORT_COLLECTIONS = [
  { id: 'tickets', label: 'Tickets' },
  { id: 'chats', label: 'Chat sessions' },
  { id: 'crm_campaigns', label: 'CRM campaigns' },
  { id: 'uptime_monitoring', label: 'Uptime monitoring' },
  { id: 'agile_tasks', label: 'Agile tasks' },
  { id: 'ai_usage', label: 'AI usage (LLM)' },
  { id: 'company_costs', label: 'Company costs' },
  { id: 'ticket_briefings', label: 'Ticket briefings' },
];

/** Filter operators accepted by the backend callable. */
export const SERVER_FILTER_OPERATORS = [
  { id: '==', label: 'Equals' },
  { id: '!=', label: 'Not equals', backendId: 'neq' },
  { id: 'contains', label: 'Contains' },
  { id: '>', label: 'Greater than' },
  { id: '<', label: 'Less than' },
  { id: '>=', label: 'Greater or equal' },
  { id: '<=', label: 'Less or equal' },
  { id: 'in', label: 'In (comma-separated)' },
  { id: 'array-contains', label: 'Array contains' },
];

/**
 * @param {string} collectionName
 */
export function getCollectionMeta(collectionName) {
  const source = getReportDataSource(collectionName);
  return {
    dimensions: source.fields.map((f) => ({ id: f.id, label: f.label })),
    filterFields: source.fields.map((f) => ({ id: f.id, label: f.label })),
    numericFields: source.numericFields.map((id) => {
      const f = source.fields.find((x) => x.id === id);
      return { id, label: f?.label || id };
    }),
  };
}

/**
 * @returns {import('./reportQueryConfig').ReportQueryConfig}
 */
export function createDefaultQueryConfig(collectionName = 'tickets') {
  const meta = getCollectionMeta(collectionName);
  const firstDim = meta.dimensions[0]?.id || 'status';
  return {
    collectionName,
    filters: [],
    dimensions: [firstDim],
    metrics: [{ type: 'count', field: '*' }],
    timeRange: { start: '', end: '' },
  };
}

/**
 * @param {ReportQueryConfig} config
 */
export function buildCallablePayload(config) {
  const timeRange = {};
  if (config.timeRange?.start) {
    timeRange.start = new Date(config.timeRange.start).toISOString();
  }
  if (config.timeRange?.end) {
    const end = new Date(config.timeRange.end);
    end.setHours(23, 59, 59, 999);
    timeRange.end = end.toISOString();
  }

  const filters = (config.filters || []).map((f) => ({
    field: f.field,
    operator: f.operator === '!=' ? 'neq' : f.operator,
    value: f.value,
  }));

  return {
    collectionName: config.collectionName,
    filters,
    dimensions: config.dimensions || [],
    metrics: config.metrics || [{ type: 'count', field: '*' }],
    timeRange: timeRange.start || timeRange.end ? timeRange : null,
  };
}

/**
 * Resolve Y-axis data key from result rows and metric config.
 * @param {Array<Record<string, unknown>>} rows
 * @param {Array<{ type: string, field: string }>} metrics
 */
export function resolveMetricDataKey(rows, metrics) {
  if (!rows?.length) return 'count';
  const sample = rows[0];
  if (sample.value !== undefined && sample.value !== null) return 'value';
  if (sample.count !== undefined && sample.count !== null) return 'count';
  if (sample.sum !== undefined && sample.sum !== null) return 'sum';
  if (sample.avg !== undefined && sample.avg !== null) return 'avg';

  const m = metrics?.[0];
  if (!m) return 'count';
  if (m.type === 'count') return 'count';
  if (m.field && m.field !== '*') return `${m.type}_${m.field}`;
  return `metric_${m.type}`;
}

export { METRIC_TYPES };

/**
 * Client-side CSV export for report result rows.
 * @param {Array<Record<string, unknown>> | null | undefined} rows
 * @param {string} collectionName
 */
export function downloadReportCsv(rows, collectionName = 'report') {
  if (!rows?.length) return;

  const keys = Object.keys(rows[0]).filter((k) => !k.startsWith('__'));
  const header = keys.join(',');
  const body = rows
    .map((row) =>
      keys
        .map((k) => {
          const v = row[k];
          const s = v === null || v === undefined ? '' : String(v);
          if (s.includes(',') || s.includes('"') || s.includes('\n')) {
            return `"${s.replace(/"/g, '""')}"`;
          }
          return s;
        })
        .join(','),
    )
    .join('\n');

  const csvContent = `${header}\n${body}`;
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute(
    'download',
    `report_${collectionName}_${new Date().toISOString().split('T')[0]}.csv`,
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
